import React, { useMemo } from 'react';
import { buildHeatmapData } from '../util/data-factory';

const LEVEL_COLORS = {
  0: '#ebedf0',
  1: '#ffaaaa',
  2: '#e62e2e',
  3: '#ff3333',
  4: '#cc2828',
  5: '#661414',
};

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function levelForCount(c) {
  if (c <= 0) return 0;
  return Math.min(5, c);
}

/** Month label for a week column: show when this week includes the 1st of a month (in-range). */
function monthLabelForWeek(week) {
  for (const cell of week) {
    if (!cell.inRange) continue;
    const d = cell.day;
    if (d.getDate() === 1) {
      return MONTH_SHORT[d.getMonth()];
    }
  }
  return '';
}

function buildWeekCells(startDate, endDate, dateCounts) {
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);

  let cur = new Date(start);
  cur.setDate(cur.getDate() - cur.getDay());

  const weeks = [];
  for (let safety = 0; safety < 45; safety++) {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(cur);
      day.setDate(cur.getDate() + i);
      const iso = day.toISOString().split('T')[0];
      const inRange = day >= start && day <= end;
      const count = inRange ? dateCounts[iso] || 0 : -1;
      days.push({ day, iso, count, inRange });
    }
    weeks.push(days);
    cur.setDate(cur.getDate() + 7);
    if (cur > end) break;
  }

  return weeks;
}

/**
 * @param {{ created_at?: string }[]} data — rows from `seen_shows` (or merged show rows with `created_at`)
 */
function SeenShowsHeatmap({ data }) {
  const { heatmapData, weeks, maxInPeriod } = useMemo(() => {
    if (!data || data.length === 0) {
      return { heatmapData: null, weeks: [], maxInPeriod: 0 };
    }

    const itemsWithDate = data.filter(item => !!item.created_at);
    if (itemsWithDate.length === 0) {
      return { heatmapData: null, weeks: [], maxInPeriod: 0 };
    }

    const hm = buildHeatmapData(itemsWithDate, 6);
    if (!hm.data || Object.keys(hm.data).length === 0) {
      return { heatmapData: null, weeks: [], maxInPeriod: 0 };
    }

    const counts = Object.values(hm.data).filter(n => n > 0);
    const maxInPeriod = counts.length ? Math.max(...counts) : 0;

    const w = buildWeekCells(hm.startDate, hm.endDate, hm.data);
    return { heatmapData: hm, weeks: w, maxInPeriod };
  }, [data]);

  if (!heatmapData || weeks.length === 0) {
    return null;
  }

  return (
    <div className="w-full pb-1">
      <div className="w-1/2 max-w-full min-w-0 mx-auto">
        <div className="flex flex-col gap-1 w-full min-w-0">
          <div className="flex gap-1.5 w-full min-w-0 items-end">
            <div className="shrink-0 w-7 md:w-8" aria-hidden />
            <div className="flex flex-1 gap-[3px] min-w-0">
              {weeks.map((week, wi) => (
                <div
                  key={`month-${wi}`}
                  className="flex-1 basis-0 min-w-0 text-center text-xs md:text-sm font-semibold text-black/65 leading-none pb-1 whitespace-nowrap overflow-visible"
                >
                  {monthLabelForWeek(week)}
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-1.5 items-stretch w-full min-w-0">
            <div className="flex flex-col justify-between shrink-0 text-[10px] md:text-[11px] font-medium text-black/50 pr-0.5 py-0.5 w-7 md:w-8">
              {DAY_LABELS.map((l, i) => (
                <span key={i} className="leading-none flex items-center justify-end tabular-nums">
                  {l}
                </span>
              ))}
            </div>
            <div className="flex flex-1 gap-[3px] min-w-0">
              {weeks.map((week, wi) => (
                <div key={wi} className="flex flex-col flex-1 gap-[3px] min-w-0 basis-0">
                  {week.map((cell, di) => {
                    if (!cell.inRange) {
                      return (
                        <div
                          key={`${cell.iso}-${di}`}
                          className="w-full aspect-square rounded-sm shrink-0 min-w-0"
                          style={{ backgroundColor: 'transparent' }}
                        />
                      );
                    }
                    const level = levelForCount(cell.count);
                    const color = LEVEL_COLORS[level] || LEVEL_COLORS[0];
                    return (
                      <div
                        key={cell.iso}
                        title={`${cell.iso}: ${cell.count} seen`}
                        className="w-full aspect-square min-w-0 rounded-sm shrink-0 flex items-center justify-center text-white font-light leading-none tabular-nums"
                        style={{
                          backgroundColor: color,
                          fontSize: 'clamp(7px, 1.8vw, 12px)',
                        }}
                      >
                        {cell.count > 0 ? cell.count : ''}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SeenShowsHeatmap;
